import { WFM_MODULES, canOpenModule, canOpenLink, firstOpenablePath, moduleDescription } from '../../config/wfmModules';
import { buildAdminEntries } from '../Admin/adminIndex';
import { highlightParts, queryTokens, scoreEntry } from '../Admin/adminSearch';

/**
 * What the global search (the box in the navbar) can find, and how it ranks it.
 *
 * Five kinds, always shown in this order:
 *   page     a module and each of its tabs: only the ones this person may open (the same rule the navbar uses)
 *   callie   Callie articles, asked of the server, which keeps them to the ones this person may see
 *   person   people in the Company Directory, asked of the server the same way
 *   admin    Admin pages, settings and actions, only for someone who can open Admin
 *   link     the external tools on the home page (everyone sees the same list there)
 *
 * The pages, Admin and the links are known to the screen and ranked here; Callie and people come back from the server already
 * ranked and already limited to what the caller may open, so nothing is hidden here that was only fetched.
 */
export const KINDS = [
  { id: 'page', label: 'Pages' },
  { id: 'callie', label: 'Callie' },
  { id: 'person', label: 'People' },
  { id: 'admin', label: 'Admin' },
  { id: 'link', label: 'Links' }
];

export const PER_KIND = 5;

/** A module and each tab of it that this person may open. */
export function pageEntries(user) {
  const out = [];
  WFM_MODULES.filter((mod) => canOpenModule(user, mod)).forEach((mod) => {
    out.push({
      id: `module:${mod.id}`,
      kind: 'page',
      title: mod.label,
      subtitle: moduleDescription(user, mod),
      description: '',
      keywords: [mod.shortLabel, mod.id].filter(Boolean),
      to: firstOpenablePath(user, mod),
      moduleId: mod.id
    });
    (mod.subLinks || []).filter((link) => canOpenLink(user, link)).forEach((link) => {
      out.push({
        id: `tab:${link.to}`,
        kind: 'page',
        title: link.label,
        subtitle: mod.label,
        description: '',
        keywords: [mod.label, mod.shortLabel].filter(Boolean),
        to: link.to,
        moduleId: mod.id
      });
    });
  });
  return out;
}

/** Admin's own pages, settings and actions (what its search offers), for someone who can open Admin at all. */
export function adminEntries(user) {
  return buildAdminEntries(user)
    .filter((entry) => entry.kind === 'page' || entry.kind === 'setting' || entry.kind === 'action')
    .map((entry) => ({ ...entry, id: `admin:${entry.id}`, kind: 'admin', subtitle: `Admin${entry.subtitle ? ` · ${entry.subtitle}` : ''}`, adminKind: entry.kind }));
}

/** The home page's external tools (groups of links). Opens in a new tab. */
export function linkEntries(groups) {
  const out = [];
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    (group.tools || []).forEach((tool) => {
      if (!tool || !tool.url) return;
      out.push({
        id: `link:${tool.id}`,
        kind: 'link',
        title: tool.label,
        subtitle: group.label || 'External tools',
        description: '',
        keywords: [group.label].filter(Boolean),
        href: tool.url
      });
    });
  });
  return out;
}

/** Callie's answer as entries: opens the article in its space. */
export function callieEntries(results) {
  return (Array.isArray(results) ? results : []).map((hit) => ({
    id: `callie:${hit.id}`,
    kind: 'callie',
    title: hit.title,
    subtitle: `${hit.spaceName || 'Callie'}${hit.type === 'folder' ? ' · folder' : ''}`,
    description: hit.snippet || '',
    keywords: [],
    // the readable address (the space's key, the article's slug) when the server sent one, else the ids, which open the same page
    to: `/knowledge-base?space=${encodeURIComponent(hit.spaceKey || hit.spaceId)}&page=${encodeURIComponent(hit.slug || hit.id)}`
  }));
}

/** The people the server found, as entries: opens the profile. */
export function personEntries(results) {
  return (Array.isArray(results) ? results : []).map((hit) => ({
    id: `person:${hit.id}`,
    kind: 'person',
    title: hit.name,
    subtitle: [hit.team, hit.position].filter(Boolean).join(' · '),
    description: hit.nickname ? `Known as ${hit.nickname}` : '',
    keywords: [],
    to: `/agent-dossier/profile/${encodeURIComponent(hit.id)}`,
    photoUrl: hit.photoUrl || '',
    archived: Boolean(hit.archived)
  }));
}

/**
 * The entries every typed word appears in, best first, at most `limit` of each kind. Exact first; only when nothing at all matched
 * is it tried again allowing a slip or two in a word, and `corrected` says so.
 */
export function searchLocal(entries, query, { limit = PER_KIND } = {}) {
  const tokens = queryTokens(query);
  if (!tokens.length) return { results: [], corrected: false };
  const run = (fuzzy) => {
    const scored = [];
    entries.forEach((entry) => {
      const score = scoreEntry(entry, tokens, { fuzzy });
      if (score >= 0) scored.push({ entry, score });
    });
    const out = [];
    KINDS.forEach((kind) => {
      scored
        .filter((hit) => hit.entry.kind === kind.id)
        .sort((a, b) => b.score - a.score || String(a.entry.title).localeCompare(String(b.entry.title)))
        .slice(0, limit)
        .forEach((hit) => out.push(hit.entry));
    });
    return out;
  };
  const exact = run(false);
  if (exact.length) return { results: exact, corrected: false };
  const close = run(true);
  return { results: close, corrected: close.length > 0 };
}

/** Local and server results in one list, grouped in the order of KINDS, with a header row before each group. */
export function groupResults(entries) {
  const rows = [];
  KINDS.forEach((kind) => {
    const inKind = entries.filter((entry) => entry.kind === kind.id);
    if (!inKind.length) return;
    rows.push({ header: kind.label });
    inKind.forEach((entry) => rows.push({ entry }));
  });
  return rows;
}

export { highlightParts, queryTokens };
